import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import appointmentService from '../services/appointmentService';
import AppointmentModal from '../components/AppointmentModal';

const STATUS_COLORS = {
  programada: '#3b82f6',
  confirmada: '#10b981',
  realizada:  '#6b7280',
  cancelada:  '#ef4444',
  reprogramada: '#f59e0b',
};

const STATUS_LABELS = {
  programada:   'Programada',
  confirmada:   'Confirmada',
  realizada:    'Realizada',
  cancelada:    'Cancelada',
  reprogramada: 'Reprogramada',
};

const TYPE_LABELS = {
  firma:       'Firma',
  consulta:    'Consulta',
  entrega:     'Entrega',
  seguimiento: 'Seguimiento',
  interno:     'Interno',
};

const LOCATION_LABELS = {
  oficina:          'Oficina',
  domicilio_cliente: 'Domicilio del cliente',
  otro:             'Otro',
};

export default function AppointmentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);

  // Cambio de status
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [createActivity, setCreateActivity] = useState(true);
  const [savingStatus, setSavingStatus] = useState(false);

  // Reprogramar
  const [showReschedule, setShowReschedule] = useState(false);
  const [rescheduleStart, setRescheduleStart] = useState('');
  const [rescheduleEnd, setRescheduleEnd] = useState('');
  const [rescheduleNotes, setRescheduleNotes] = useState('');
  const [savingReschedule, setSavingReschedule] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await appointmentService.getById(id);
      setAppointment(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const handleStatusSubmit = async (e) => {
    e.preventDefault();
    setSavingStatus(true);
    try {
      await appointmentService.updateStatus(id, {
        status: newStatus,
        notes: statusNotes,
        cancellation_reason: cancelReason,
        create_activity: createActivity,
      });
      setShowStatusModal(false);
      setStatusNotes('');
      setCancelReason('');
      load();
    } catch (err) {
      console.error(err);
    } finally {
      setSavingStatus(false);
    }
  };

  const handleReschedule = async (e) => {
    e.preventDefault();
    setSavingReschedule(true);
    try {
      const res = await appointmentService.reschedule(id, {
        start_datetime: rescheduleStart,
        end_datetime: rescheduleEnd,
        notes: rescheduleNotes,
      });
      setShowReschedule(false);
      navigate(`/agenda/${res.data.id}`);
    } catch (err) {
      console.error(err);
    } finally {
      setSavingReschedule(false);
    }
  };

  const openStatusModal = (status) => {
    setNewStatus(status);
    setStatusNotes('');
    setCancelReason('');
    setCreateActivity(true);
    setShowStatusModal(true);
  };

  if (loading) return <div className="text-center py-5"><div className="spinner-border text-primary" /></div>;
  if (!appointment) return <div className="container py-4"><div className="alert alert-danger">Cita no encontrada</div></div>;

  const start = new Date(appointment.start_datetime);
  const end = new Date(appointment.end_datetime);
  const duration = Math.round((end - start) / 60000);

  const isClosed = ['realizada', 'cancelada'].includes(appointment.status);

  return (
    <div className="container py-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-start mb-4 flex-wrap gap-2">
        <div>
          <button className="btn btn-sm btn-outline-secondary mb-2" onClick={() => navigate('/agenda')}>
            ← Regresar
          </button>
          <h4 className="mb-1">{appointment.title}</h4>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span className="badge" style={{ background: STATUS_COLORS[appointment.status], color: '#fff', fontSize: 13 }}>
              {STATUS_LABELS[appointment.status]}
            </span>
            <span className="text-muted" style={{ fontSize: 13 }}>{TYPE_LABELS[appointment.type]}</span>
            <span className="text-muted" style={{ fontSize: 13 }}>· {LOCATION_LABELS[appointment.location]}</span>
          </div>
        </div>

        {!isClosed && (
          <div className="d-flex gap-2 flex-wrap">
            <button className="btn btn-sm btn-outline-primary" onClick={() => setShowEdit(true)}>Editar</button>
            <button className="btn btn-sm btn-outline-warning" onClick={() => setShowReschedule(true)}>Reprogramar</button>
            {appointment.status === 'programada' && (
              <button className="btn btn-sm btn-success" onClick={() => openStatusModal('confirmada')}>Confirmar</button>
            )}
            {appointment.status === 'confirmada' && (
              <button className="btn btn-sm btn-success" onClick={() => openStatusModal('realizada')}>Marcar realizada</button>
            )}
            {appointment.status === 'programada' && (
              <button className="btn btn-sm btn-success" onClick={() => openStatusModal('realizada')}>Marcar realizada</button>
            )}
            <button className="btn btn-sm btn-danger" onClick={() => openStatusModal('cancelada')}>Cancelar cita</button>
          </div>
        )}
      </div>

      <div className="row">
        {/* Info principal */}
        <div className="col-md-8">
          <div className="card mb-4">
            <div className="card-header"><strong>Información de la cita</strong></div>
            <div className="card-body">
              <div className="row">
                <div className="col-sm-6 mb-3">
                  <small className="text-muted d-block">Fecha y hora de inicio</small>
                  <strong>{start.toLocaleString('es-MX', { dateStyle: 'full', timeStyle: 'short' })}</strong>
                </div>
                <div className="col-sm-6 mb-3">
                  <small className="text-muted d-block">Hora de fin</small>
                  <strong>{end.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })} ({duration} min)</strong>
                </div>
                <div className="col-sm-6 mb-3">
                  <small className="text-muted d-block">Responsable</small>
                  <strong>{appointment.user?.name || '—'}</strong>
                </div>
                <div className="col-sm-6 mb-3">
                  <small className="text-muted d-block">Creada por</small>
                  <strong>{appointment.created_by_user?.name || appointment.createdBy?.name || '—'}</strong>
                </div>
                {appointment.notes && (
                  <div className="col-12">
                    <small className="text-muted d-block">Notas</small>
                    <p className="mb-0">{appointment.notes}</p>
                  </div>
                )}
                {appointment.cancellation_reason && (
                  <div className="col-12 mt-2">
                    <small className="text-muted d-block">Motivo de cancelación</small>
                    <p className="mb-0 text-danger">{appointment.cancellation_reason}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Historial de cambios */}
          <div className="card">
            <div className="card-header"><strong>Historial de cambios</strong></div>
            <div className="card-body p-0">
              {appointment.activity_logs?.length === 0 && (
                <div className="text-center text-muted py-3">Sin cambios registrados</div>
              )}
              <ul className="list-group list-group-flush">
                {appointment.activity_logs?.map(log => (
                  <li key={log.id} className="list-group-item">
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <span className="text-muted" style={{ fontSize: 12 }}>{log.user?.name}</span>
                        <div style={{ fontSize: 13 }}>
                          {log.previous_status
                            ? <><span className="badge badge-secondary">{STATUS_LABELS[log.previous_status]}</span> → <span className="badge" style={{ background: STATUS_COLORS[log.new_status], color: '#fff' }}>{STATUS_LABELS[log.new_status]}</span></>
                            : <span className="badge" style={{ background: STATUS_COLORS[log.new_status], color: '#fff' }}>{STATUS_LABELS[log.new_status]}</span>
                          }
                        </div>
                        {log.notes && <div className="text-muted mt-1" style={{ fontSize: 12 }}>{log.notes}</div>}
                      </div>
                      <small className="text-muted">
                        {new Date(log.created_at).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })}
                      </small>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="col-md-4">
          {/* Cliente */}
          <div className="card mb-3">
            <div className="card-header"><strong>Cliente</strong></div>
            <div className="card-body">
              {appointment.client ? (
                <>
                  <div><strong>{appointment.client.name}</strong></div>
                  <div className="text-muted" style={{ fontSize: 13 }}>{appointment.client.phone}</div>
                  <div className="text-muted" style={{ fontSize: 13 }}>{appointment.client.email}</div>
                  <Link to={`/clients/${appointment.client.id}`} className="btn btn-sm btn-outline-primary mt-2">
                    Ver cliente
                  </Link>
                </>
              ) : (
                <span className="text-muted">Sin cliente vinculado</span>
              )}
            </div>
          </div>

          {/* Expediente */}
          <div className="card mb-3">
            <div className="card-header"><strong>Expediente</strong></div>
            <div className="card-body">
              {appointment.case_file ? (
                <>
                  <div><strong>#{appointment.case_file.id}</strong></div>
                  <div className="text-muted" style={{ fontSize: 13 }}>{appointment.case_file.matter}</div>
                  <Link to={`/case-files/${appointment.case_file.id}`} className="btn btn-sm btn-outline-primary mt-2">
                    Ver expediente
                  </Link>
                </>
              ) : (
                <span className="text-muted">Sin expediente vinculado</span>
              )}
            </div>
          </div>

          {/* Reprogramada desde */}
          {appointment.rescheduled_from && (
            <div className="card mb-3">
              <div className="card-header"><strong>Reprogramada desde</strong></div>
              <div className="card-body">
                <Link to={`/agenda/${appointment.rescheduled_from.id}`} className="btn btn-sm btn-outline-secondary">
                  Ver cita original #{appointment.rescheduled_from.id}
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal editar */}
      {showEdit && (
        <AppointmentModal
          appointment={appointment}
          onClose={() => setShowEdit(false)}
          onSaved={() => { setShowEdit(false); load(); }}
        />
      )}

      {/* Modal cambio de status */}
      {showStatusModal && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {newStatus === 'cancelada' ? 'Cancelar cita' : newStatus === 'realizada' ? 'Marcar como realizada' : 'Confirmar cita'}
                </h5>
                <button className="close" onClick={() => setShowStatusModal(false)}><span>&times;</span></button>
              </div>
              <form onSubmit={handleStatusSubmit}>
                <div className="modal-body">
                  {newStatus === 'cancelada' && (
                    <div className="form-group">
                      <label>Motivo de cancelación</label>
                      <textarea className="form-control" rows={3} value={cancelReason} onChange={e => setCancelReason(e.target.value)} placeholder="Indica el motivo..." />
                    </div>
                  )}
                  <div className="form-group">
                    <label>Notas adicionales</label>
                    <textarea className="form-control" rows={2} value={statusNotes} onChange={e => setStatusNotes(e.target.value)} placeholder="Observaciones..." />
                  </div>
                  {newStatus === 'realizada' && appointment.case_file_id && (
                    <div className="form-check">
                      <input type="checkbox" className="form-check-input" id="createActivity" checked={createActivity} onChange={e => setCreateActivity(e.target.checked)} />
                      <label className="form-check-label" htmlFor="createActivity">
                        Registrar actividad automáticamente en el expediente
                      </label>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowStatusModal(false)}>Cancelar</button>
                  <button type="submit" className={`btn ${newStatus === 'cancelada' ? 'btn-danger' : 'btn-success'}`} disabled={savingStatus}>
                    {savingStatus ? 'Guardando...' : 'Confirmar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal reprogramar */}
      {showReschedule && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Reprogramar cita</h5>
                <button className="close" onClick={() => setShowReschedule(false)}><span>&times;</span></button>
              </div>
              <form onSubmit={handleReschedule}>
                <div className="modal-body">
                  <div className="form-group">
                    <label>Nueva fecha y hora de inicio <span className="text-danger">*</span></label>
                    <input type="datetime-local" className="form-control" value={rescheduleStart} onChange={e => setRescheduleStart(e.target.value)} required />
                  </div>
                  <div className="form-group">
                    <label>Nueva fecha y hora de fin <span className="text-danger">*</span></label>
                    <input type="datetime-local" className="form-control" value={rescheduleEnd} onChange={e => setRescheduleEnd(e.target.value)} required />
                  </div>
                  <div className="form-group">
                    <label>Motivo del cambio</label>
                    <textarea className="form-control" rows={2} value={rescheduleNotes} onChange={e => setRescheduleNotes(e.target.value)} placeholder="¿Por qué se reprograma?" />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowReschedule(false)}>Cancelar</button>
                  <button type="submit" className="btn btn-warning" disabled={savingReschedule}>
                    {savingReschedule ? 'Guardando...' : 'Reprogramar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}